import { useState } from "react";
import { toast } from "sonner";
import { Plus, Trash2, Pencil } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { PageHeader, Panel, Field, Empty, NeedClinic } from "@/components/common";
import { useAuth } from "@/context/AuthContext";
import { api, errMsg, money, useLoad } from "@/lib/api";
import { can, CATEGORY_LABELS } from "@/lib/constants";

export const allowedCategories = (clinic) =>
  Object.keys(CATEGORY_LABELS).filter((k) => (k !== "dental" || clinic?.modules?.dental) && (k !== "procedure" || clinic?.modules?.procedures));

export default function Services() {
  const { user, activeClinic, activeClinicId } = useAuth();
  const [rows, reload] = useLoad(activeClinicId ? "/services" : null, { clinic_id: activeClinicId });
  const [f, setF] = useState(null);
  const manage = can(user, "manageServices");
  if (!activeClinic) return <NeedClinic isCeo={user.role === "ceo"} />;
  const save = async () => {
    try {
      const body = { name: f.name, category: f.category, price: Number(f.price || 0) };
      if (f.id) await api.put(`/services/${f.id}`, body); else await api.post("/services", body);
      toast.success("Service saved");
      setF(null);
      reload();
    } catch (e) { toast.error(errMsg(e)); }
  };
  const del = async (id) => {
    try { await api.delete(`/services/${id}`); reload(); } catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <>
      <PageHeader eyebrow={activeClinic.name} title="Service catalogue" subtitle="Price list used when building invoices.">
        {manage && <Button className="rounded-full" onClick={() => setF({ name: "", category: "consultation", price: "" })} data-testid="add-service-button"><Plus className="mr-2 h-4 w-4" />Add service</Button>}
      </PageHeader>
      <Panel testId="services-table">
        {!rows?.length ? <Empty text="No services in the catalogue yet." /> : (
          <Table>
            <TableHeader><TableRow><TableHead>Service</TableHead><TableHead>Category</TableHead><TableHead className="text-right">Price</TableHead>{manage && <TableHead />}</TableRow></TableHeader>
            <TableBody>{rows.map((s) => (
              <TableRow key={s.id} data-testid={`service-row-${s.id}`}>
                <TableCell className="font-semibold">{s.name}</TableCell>
                <TableCell>{CATEGORY_LABELS[s.category]}</TableCell>
                <TableCell className="text-right font-mono">{money(s.price, activeClinic.currency)}</TableCell>
                {manage && <TableCell className="text-right whitespace-nowrap">
                  <Button size="icon" variant="ghost" onClick={() => setF({ ...s })} data-testid={`service-edit-${s.id}`}><Pencil className="h-4 w-4" /></Button>
                  <Button size="icon" variant="ghost" onClick={() => del(s.id)} data-testid={`service-delete-${s.id}`}><Trash2 className="h-4 w-4 text-rose-600" /></Button>
                </TableCell>}
              </TableRow>))}</TableBody>
          </Table>)}
      </Panel>
      <Dialog open={!!f} onOpenChange={(o) => !o && setF(null)}>
        <DialogContent>
          <DialogHeader><DialogTitle>{f?.id ? "Edit service" : "New service"}</DialogTitle></DialogHeader>
          {f && <div className="grid gap-4">
            <Field label="Name"><Input value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} data-testid="service-name-input" /></Field>
            <Field label="Category"><Select value={f.category} onValueChange={(v) => setF({ ...f, category: v })}>
              <SelectTrigger data-testid="service-category-select"><SelectValue /></SelectTrigger>
              <SelectContent>{allowedCategories(activeClinic).map((k) => <SelectItem key={k} value={k}>{CATEGORY_LABELS[k]}</SelectItem>)}</SelectContent></Select></Field>
            <Field label={`Price (${activeClinic.currency})`}><Input type="number" min="0" value={f.price} onChange={(e) => setF({ ...f, price: e.target.value })} data-testid="service-price-input" /></Field>
          </div>}
          <DialogFooter><Button onClick={save} data-testid="service-form-submit-button">Save</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
